import { useState } from 'react'
import { cls } from '@/ui/cls'
import { RATING_TIER } from '@/shared/domain'

interface Props {
  value: number
  onChange?: (n: number) => void
  size?: number
  readOnly?: boolean
  noHint?: boolean   // suppress the hover-text label so width stays stable in dense layouts
  title?: string
}

export default function StarRating({ value, onChange, size = 22, readOnly = false, noHint = false, title }: Props) {
  const [hover, setHover] = useState(0)
  const stars = [1, 2, 3, 4, 5]
  return (
    <div
      className={cls(
        'inline-flex gap-1 items-center',
        readOnly && 'read-only',
      )}
      onMouseLeave={() => setHover(0)}
      title={title}
    >
      {stars.map(n => {
        const filled = (hover || value) >= n
        return (
          // `star` + `star.on` are kept as className hooks for the hover-pop
          // animation defined in app.css (app-pop keyframes).
          <button
            key={n}
            type="button"
            className={cls(
              'star bg-transparent border-0 p-0.5 grid place-items-center',
              readOnly ? 'cursor-default' : 'cursor-pointer',
              filled && 'on',
            )}
            disabled={readOnly}
            onMouseEnter={() => !readOnly && setHover(n)}
            onClick={() => !readOnly && onChange?.(n)}
            style={{ width: size, height: size }}
            aria-label={`${n} av 5`}
          >
            <svg viewBox="0 0 24 24" width={size - 4} height={size - 4}>
              <path
                d="M12 2.6l2.95 5.97 6.59.96-4.77 4.65 1.13 6.57L12 17.66l-5.9 3.1 1.13-6.57L2.46 9.53l6.59-.96L12 2.6z"
                fill={filled ? 'var(--color-brand)' : 'transparent'}
                stroke={filled ? 'var(--color-brand-deep)' : 'var(--color-ink-300)'}
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )
      })}
      {!readOnly && !noHint && hover > 0 && (
        <span className="text-xs text-ink-500 ml-2">{RATING_TIER[hover]}</span>
      )}
    </div>
  )
}
